import { ChangeEvent, useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { unwrapResult } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { AddOutlined as Add, DeleteOutlineOutlined as Delete } from '@mui/icons-material';
import { SelectChangeEvent } from '@mui/material';

import CustomButton from '../../../../../shared/uiComponents/Button';
import Picker from '../../../../../shared/uiComponents/DateAndTime';
import {
	dateNormalizer,
	errorNormalizer,
	validateDecimalNumber,
	validateNumber,
} from '../../../../../shared/Helpers/functions';
import Text from '../../../../../shared/uiComponents/Text';
import { CustomAlert, CustomInput, CustomSelect } from '../../../../../shared/uiComponents';
import { useCompareDates } from '../../../../../shared/Helpers/hooks';

import { DispatchProperties, useSelector } from '../../../../../redux/store';
import { getClientFunders } from '../../../../../redux/State/clientSlice/fundingSlice';
import {
	getInsuranceAuthorizationSessionTypes,
	getInsuranceAuthorizations,
	saveTreatmentInsuranceAuthorizations,
} from '../../../../../redux/State/clientSlice/insuranceAuthorizationSlice';
import {
	AddAuthorizationDetailsProperties,
	AuthorizationProperties,
	InsuranceAuthorizationSessionTypeProperties,
} from '../../../../../redux/API/ClientAPIHelpers/insuranceAuthorizationProperties';
import { useCalculateHoursPerWeek } from '../hook';

interface DetailsInfoProperties extends AddAuthorizationDetailsProperties {
	name: string;
}

const TreatmentContent = ({ onClose }: { onClose: () => void }) => {
	const today = new Date();
	const todayDayjs = dayjs(today);
	const futureDateWeek = dayjs(today).add(1, 'week');

	const dispatch = useDispatch<DispatchProperties>();
	const { clientId } = useParams();
	const [params] = useSearchParams();

	const clientFunders = useSelector((state) => state.funding.clientFunders);
	const [selectedFunderId, setSelectedFunderId] = useState<string>('insurance');

	const [authInfo, setAuthInfo] = useState<InsuranceAuthorizationSessionTypeProperties>({
		cptCode: null,
		id: '',
		frequencies: [],
		name: '',
		sessionTypeId: null,
		supportAdditionalWeeklyHours: false,
	});
	const [frequency, setFrequency] = useState<number>(1);
	const [totalHours, setTotalHours] = useState<string>('');
	const [customHoursPerWeek, setCustomHoursPerWeek] = useState<string>('');
	const [loadingSaving, setLoadingSaving] = useState<boolean>(false);

	const [startDateValue, setStartDateValue] = useState<Dayjs>(todayDayjs);
	const [endDateValue, setEndDateValue] = useState<Dayjs>(futureDateWeek);
	const { hasError, message } = useCompareDates(startDateValue, endDateValue);
	const { hoursPerWeek, loading } = useCalculateHoursPerWeek({
		startDateValue,
		endDateValue,
		totalHours,
		frequency,
	});

	const [treatmentAuthorizationDetails, setTreatmentAuthorizationDetails] = useState<DetailsInfoProperties[]>([]);
	const [sessionTypesData, setSessionTypesData] = useState<InsuranceAuthorizationSessionTypeProperties[]>([]);
	const sessionTypes = useSelector((state) => state.authorization.insuranceAuthorizationSessionTypes);

	useEffect(() => {
		if (authInfo.name.includes('Meet')) {
			setTotalHours('1');
		}
	}, [authInfo.name]);

	useEffect(() => {
		if (!sessionTypes?.length) return;
		const frequencyValue = sessionTypes.find((x) => x.id === authInfo.id);
		if (!frequencyValue) return;
		const frequencies = [...frequencyValue.frequencies];
		setFrequency(
			frequencies.sort((a, b) => {
				if (a.frequency > b.frequency) return 1;
				if (a.frequency < b.frequency) return -1;

				return 0;
			})[0].id,
		);
	}, [sessionTypes, authInfo]);

	useEffect(() => {
		if (!totalHours.length) return;
		setCustomHoursPerWeek(`${hoursPerWeek}`);
	}, [totalHours, hoursPerWeek]);

	const onChangeTreatmentStartDate = (value: Dayjs | null) => {
		if (!value) return;
		setStartDateValue(value);
	};

	const onChangeTreatmentEndDate = (value: Dayjs | null) => {
		if (!value) return;
		setEndDateValue(value);
	};

	const onChangeSessionType = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		const sessionTypeInfo = sessionTypes.find((x) => x.id === value);
		if (!sessionTypeInfo) return;
		setAuthInfo(sessionTypeInfo);
		setTotalHours('0');
	};

	const fetchAuthorizations = () => {
		const page = params.get('page') || '1';
		const pageSize = params.get('pageSize') || '8';
		if (!clientId) return;
		dispatch(getInsuranceAuthorizations({ clientId, page, pageSize }));
	};

	const saveAuthorizationTreatment = () => {
		if (!startDateValue || !endDateValue || !clientId) return;

		const startDate = dateNormalizer(startDateValue);
		const endDate = dateNormalizer(endDateValue);

		const data: AuthorizationProperties = {
			startDate,
			endDate,
			authorizationDetails: treatmentAuthorizationDetails,
			funderId: selectedFunderId === 'insurance' ? null : selectedFunderId,
		};
		setLoadingSaving(true);

		dispatch(saveTreatmentInsuranceAuthorizations({ clientId, data }))
			.then(unwrapResult)
			.then(() => {
				onClose();
				toast('Success');
				fetchAuthorizations();
			})
			.catch(errorNormalizer)
			.finally(() => setLoadingSaving(false));
	};

	const frequencyHandler = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		if (!validateNumber(value)) return;
		setFrequency(parseInt(value));
	};

	const onTotalHoursChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		if (!validateDecimalNumber(value)) return;
		setTotalHours(value);
		setCustomHoursPerWeek('');
	};

	const onHoursPerWeekChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		if (!validateDecimalNumber(value)) return;
		setCustomHoursPerWeek(value);
	};

	const addTreatmentAuthorizationDetail = () => {
		if (!totalHours || (frequency === 1 && !parseFloat(customHoursPerWeek))) return;
		const { id, name, sessionTypeId, cptCode } = authInfo;
		const item: DetailsInfoProperties = {
			authorizationSessionTypeId: id,
			cptCode,
			totalHours: parseFloat(totalHours),
			frequency,
			name,
			hoursPerWeek: frequency === 1 ? parseFloat(customHoursPerWeek) : null,
			sessionTypeId,
		};
		setTreatmentAuthorizationDetails((prev) => [...prev, item]);
		const types = funderScopedSessionTypesData.length
			? funderScopedSessionTypesData.filter((x) => x.id !== id)
			: funderScopedSessionTypes.filter((x) => x.id !== id);
		setSessionTypesData(types);
		setTotalHours('');
		setCustomHoursPerWeek('');
		setAuthInfo({
			cptCode: null,
			frequencies: [],
			id: '',
			name: '',
			sessionTypeId: null,
			supportAdditionalWeeklyHours: false,
		});
	};

	const removeHandler = (sessionId: string) => {
		const items = treatmentAuthorizationDetails.filter((x) => x.authorizationSessionTypeId !== sessionId);
		setTreatmentAuthorizationDetails(items);
		const type = sessionTypes.find((x) => x.id === sessionId);
		if (!type) return;
		const types = [...sessionTypesData, type];
		setSessionTypesData(types);
	};

	const sessionFrequencyTypesData =
		sessionTypes
			.find((x) => x.id === authInfo.id)
			?.frequencies.map((x) => ({
				id: `${x.id}`,
				label: x.name,
			})) ?? [];

	// Only offer funders that have at least one authorization session type configured.
	// Funders without auth catalog entries (e.g. ELC, RequiresAuthorization=false) are
	// excluded so the dropdown never leads to a dead-end with no session types to pick.
	const fundersWithAuthTypes = clientFunders.filter((f) =>
		sessionTypes.some((st) => st.funderId === f.funderId),
	);

	const funderOptions = [
		{ id: 'insurance', name: 'Insurance' },
		...fundersWithAuthTypes.map((f) => ({ id: f.funderId, name: f.funderName })),
	];

	const onFunderChange = (event: SelectChangeEvent<string>) => {
		setSelectedFunderId(event.target.value);
		// Switching funding source invalidates any in-progress selection and any already-added
		// detail rows (they belonged to the previous funder) — clear both so insurance and DOE
		// codes can never end up mixed on the same authorization.
		setAuthInfo({
			cptCode: null,
			frequencies: [],
			id: '',
			name: '',
			sessionTypeId: null,
			supportAdditionalWeeklyHours: false,
		});
		setTreatmentAuthorizationDetails([]);
		setSessionTypesData([]);
	};

	useEffect(() => {
		if (!clientId) return;
		dispatch(getClientFunders(clientId));
	}, [dispatch, clientId]);

	useEffect(() => {
		if (fundersWithAuthTypes.length === 1) {
			setSelectedFunderId(fundersWithAuthTypes[0].funderId);
		}
	}, [fundersWithAuthTypes]);

	// Only show session types that belong to the currently selected funding source:
	// insurance codes (funderId undefined/null) when "Insurance (default)" is selected,
	// or the matching funder's codes when a funder is selected. Prevents insurance and
	// DOE codes from ever being co-mingled on the same authorization.
	const funderScopedSessionTypes = sessionTypes.filter((x) =>
		selectedFunderId === 'insurance' ? !x.funderId : x.funderId === selectedFunderId,
	);
	const funderScopedSessionTypesData = sessionTypesData.filter((x) =>
		selectedFunderId === 'insurance' ? !x.funderId : x.funderId === selectedFunderId,
	);

	return (
		<>
			{fundersWithAuthTypes.length > 0 && (
				<CustomSelect
					label="Funding Source"
					data={funderOptions}
					value={selectedFunderId}
					setValue={onFunderChange}
					className="marginBottom16"
				/>
			)}
			<div className="dateRow">
				<Picker.CustomDate
					label="Start Date:"
					value={startDateValue}
					onChange={onChangeTreatmentStartDate}
					className="marginBottom16 marginTop16"
					disabled={Boolean(treatmentAuthorizationDetails.length)}
				/>
				<Picker.CustomDate
					label="End Date:"
					value={endDateValue}
					onChange={onChangeTreatmentEndDate}
					errorMessage={message}
					disabled={Boolean(treatmentAuthorizationDetails.length)}
					error={hasError}
				/>
			</div>
			{treatmentAuthorizationDetails.map((x) => (
				<div className="authorizationDetailCard padding8 marginBottom8" key={x.name}>
					<Text title={`Type: ${x.name}`} type="psm" />
					<Text title={`Frequency: ${x.frequency === 1 ? 'Weekly' : 'Entire period'}`} type="psm" />
					<Text title={`Hours: ${x.totalHours}`} type="psm" />
					{x.frequency === 1 && <Text title={`Weekly limit: ${x.hoursPerWeek}`} type="psm" />}
					<Delete className="deleteIcon margin8" onClick={() => removeHandler(x.authorizationSessionTypeId)} />
				</div>
			))}
			<div className="sessionTypeRow">
				{treatmentAuthorizationDetails.length !== funderScopedSessionTypes.length && (
					<div className="fullWidth">
						<CustomSelect
							label="Choose session type:"
							data={funderScopedSessionTypesData.length ? funderScopedSessionTypesData : funderScopedSessionTypes}
							value={authInfo.id}
							setValue={onChangeSessionType}
							className="marginBottom16"
						/>
					</div>
				)}
				{Boolean(authInfo.id) && (
					<CustomSelect
						label="Frequency"
						data={sessionFrequencyTypesData}
						value={`${frequency}`}
						className="width150"
						setValue={frequencyHandler}
					/>
				)}
			</div>
			{Boolean(authInfo.id) && (
				<div className="hoursRow marginBottom8">
					<CustomInput label="Total hours" value={totalHours} onChange={onTotalHoursChange} />
					{!loading && (
						<div className="hoursRow">
							{frequency === 1 && (
								<CustomInput label="Weekly limit" value={customHoursPerWeek} onChange={onHoursPerWeekChange} />
							)}
							{(frequency !== 1 || Boolean(parseFloat(customHoursPerWeek))) && (
								<Add onClick={addTreatmentAuthorizationDetail} fontSize="large" className="marginTop24" />
							)}
						</div>
					)}
				</div>
			)}
			<CustomButton
				title="Save Treatment"
				onClick={saveAuthorizationTreatment}
				disabled={hasError || !treatmentAuthorizationDetails.length}
				loading={loadingSaving}
			/>
		</>
	);
};

const AddTreatmentInsuranceAuthorization = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const [open, setOpen] = useState<boolean>(false);

	const openHandler = () => {
		dispatch(getInsuranceAuthorizationSessionTypes('2'));
		setOpen(true);
	};
	const closeHandler = () => setOpen(false);

	return (
		<>
			<CustomButton title="Add Treatment" onClick={openHandler} />
			<CustomAlert
				title="Treatment"
				open={open}
				onClose={closeHandler}
				contentHeight="550px"
				Content={({ onClose }) => <TreatmentContent onClose={onClose} />}
			/>
		</>
	);
};

export default AddTreatmentInsuranceAuthorization;
